import { useRef, useState } from 'react';
import { Upload, CheckCircle2, AlertCircle, X } from 'lucide-react';
import { parseBwaFile, type FileParseResult } from '../../lib/bwa/parseFile';
import { Button } from '../ui/Button';

interface BwaUploadProps {
  onParsed: (result: { revenueCents: number; costsCents: number; yearMonth?: string }) => void;
}

type UiState =
  | { kind: 'idle' }
  | { kind: 'parsing'; filename: string }
  | { kind: 'preview'; filename: string; result: FileParseResult }
  | { kind: 'error'; filename: string; hint: string };

/**
 * BWA-Upload-Komponente (AP9).
 *
 * Editorial-Stil: dezenter Upload-Bereich mit gestricheltem Rahmen, kein
 * großer „Choose File"-Button. Nach erfolgreichem Parse wird eine Vorschau
 * der erkannten Werte angezeigt; der Berater übernimmt sie per Klick in
 * das Formular.
 *
 * PDF-Dateien werden erkannt aber abgelehnt mit klarem Hinweis auf
 * AP9b-Roadmap.
 */
export function BwaUpload({ onParsed }: BwaUploadProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [state, setState] = useState<UiState>({ kind: 'idle' });

  async function handleFile(file: File): Promise<void> {
    setState({ kind: 'parsing', filename: file.name });
    try {
      const result = await parseBwaFile(file);
      if (result.status === 'unsupported_format' || result.status === 'read_error') {
        setState({ kind: 'error', filename: file.name, hint: result.hint });
        return;
      }
      setState({ kind: 'preview', filename: file.name, result });
    } catch (err) {
      setState({
        kind: 'error',
        filename: file.name,
        hint: err instanceof Error ? err.message : 'Unbekannter Fehler beim Parsen.',
      });
    }
  }

  function handleConfirm(): void {
    if (state.kind !== 'preview') return;
    const r = state.result;
    if (r.status === 'ok') {
      onParsed({
        revenueCents: r.month.revenueCents,
        costsCents: r.month.costsCents,
        yearMonth: r.month.yearMonth,
      });
    } else if (
      r.status === 'incomplete' &&
      r.partial.revenueCents !== undefined &&
      r.partial.costsCents !== undefined
    ) {
      onParsed({
        revenueCents: r.partial.revenueCents,
        costsCents: r.partial.costsCents,
      });
    }
    reset();
  }

  function reset(): void {
    setState({ kind: 'idle' });
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <section className="rounded-sm border border-dashed border-stone-300 bg-stone-50/50 p-5 md:p-6">
      <div className="flex items-start gap-4">
        <div className="hidden md:block">
          <Upload size={20} className="mt-1 text-stone-500" />
        </div>
        <div className="flex-1">
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            BWA-Import (optional)
          </p>
          <h3 className="mt-1 font-serif text-base font-bold text-stone-900">
            DATEV-BWA als XLSX hochladen
          </h3>
          <p className="mt-1 max-w-prose font-serif text-sm italic text-stone-500">
            Aus DATEV als „Excel-Datei" exportierte BWA wird automatisch ausgewertet. Die erkannten
            Werte können Sie vor der Übernahme prüfen.
          </p>

          {state.kind === 'idle' && (
            <div className="mt-4 flex flex-col gap-2 md:flex-row md:items-center">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => inputRef.current?.click()}
              >
                Datei wählen →
              </Button>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls,.xlsm,.pdf"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleFile(file);
                }}
              />
              <span className="font-sans text-xs uppercase tracking-widest text-stone-500">
                XLSX, XLS · PDF folgt in AP9b
              </span>
            </div>
          )}

          {state.kind === 'parsing' && (
            <p className="mt-4 font-sans text-sm text-stone-500">Analysiere {state.filename}…</p>
          )}

          {state.kind === 'error' && (
            <div className="mt-4 flex items-start gap-2 rounded-sm bg-red-50 px-3 py-2 text-red-800">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-sans text-xs font-medium uppercase tracking-widest">
                  {state.filename}
                </p>
                <p className="mt-1 font-serif text-sm">{state.hint}</p>
                <button
                  type="button"
                  onClick={reset}
                  className="mt-2 font-sans text-xs font-medium uppercase tracking-widest underline"
                >
                  Andere Datei wählen
                </button>
              </div>
            </div>
          )}

          {state.kind === 'preview' && state.result.status === 'ok' && (
            <PreviewBlock
              filename={state.filename}
              revenueCents={state.result.month.revenueCents}
              costsCents={state.result.month.costsCents}
              yearMonth={state.result.month.yearMonth}
              hint={
                state.result.source.yearMonthSource === 'sheetname'
                  ? `Auswertungszeitraum aus Sheet-Name „${state.result.sheetName}" abgeleitet`
                  : 'Auswertungszeitraum aus Datei-Header gelesen'
              }
              onConfirm={handleConfirm}
              onReset={reset}
            />
          )}

          {state.kind === 'preview' &&
            state.result.status === 'incomplete' &&
            state.result.partial.revenueCents !== undefined &&
            state.result.partial.costsCents !== undefined && (
              <PreviewBlock
                filename={state.filename}
                revenueCents={state.result.partial.revenueCents}
                costsCents={state.result.partial.costsCents}
                hint={state.result.hint}
                onConfirm={handleConfirm}
                onReset={reset}
              />
            )}

          {state.kind === 'preview' && state.result.status === 'unknown_format' && (
            <div className="mt-4 flex items-start gap-2 rounded-sm bg-amber-50 px-3 py-2 text-amber-800">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-sans text-xs font-medium uppercase tracking-widest">
                  Format nicht erkannt
                </p>
                <p className="mt-1 font-serif text-sm">{state.result.hint}</p>
                <button
                  type="button"
                  onClick={reset}
                  className="mt-2 font-sans text-xs font-medium uppercase tracking-widest underline"
                >
                  Andere Datei wählen
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function formatEuro(cents: number): string {
  const euros = Math.round(cents / 100);
  return `${euros.toLocaleString('de-DE')} €`;
}

interface PreviewProps {
  filename: string;
  revenueCents: number;
  costsCents: number;
  yearMonth?: string;
  hint: string;
  onConfirm: () => void;
  onReset: () => void;
}

function PreviewBlock({
  filename,
  revenueCents,
  costsCents,
  yearMonth,
  hint,
  onConfirm,
  onReset,
}: PreviewProps) {
  const profit = revenueCents - costsCents;
  return (
    <div className="mt-4 rounded-sm border border-emerald-300 bg-emerald-50/40 px-4 py-3">
      <div className="flex items-start gap-2 text-emerald-800">
        <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="font-sans text-xs font-medium uppercase tracking-widest">
            Werte erkannt aus {filename}
            {yearMonth ? ` · ${yearMonth}` : ''}
          </p>
          <p className="mt-1 font-serif text-xs italic">{hint}</p>
        </div>
        <button
          type="button"
          onClick={onReset}
          aria-label="Zurücksetzen"
          className="text-emerald-600 hover:text-emerald-800"
        >
          <X size={14} />
        </button>
      </div>

      <dl className="mt-3 grid grid-cols-3 gap-3 border-t border-emerald-200/60 pt-3 text-stone-900">
        <div>
          <dt className="font-sans text-xs uppercase tracking-widest text-stone-500">Umsatz</dt>
          <dd className="mt-1 font-serif text-lg font-bold tabular-nums">
            {formatEuro(revenueCents)}
          </dd>
        </div>
        <div>
          <dt className="font-sans text-xs uppercase tracking-widest text-stone-500">Kosten</dt>
          <dd className="mt-1 font-serif text-lg font-bold tabular-nums">
            {formatEuro(costsCents)}
          </dd>
        </div>
        <div>
          <dt className="font-sans text-xs uppercase tracking-widest text-stone-500">Gewinn</dt>
          <dd
            className={`mt-1 font-serif text-lg font-bold tabular-nums ${
              profit >= 0 ? 'text-stone-900' : 'text-red-700'
            }`}
          >
            {formatEuro(profit)}
          </dd>
        </div>
      </dl>

      <button
        type="button"
        onClick={onConfirm}
        className="mt-4 rounded-sm bg-stone-900 px-4 py-2 font-sans text-xs font-medium uppercase tracking-widest text-white transition-colors hover:bg-stone-800"
      >
        Werte übernehmen →
      </button>
    </div>
  );
}
